<main class="shift-summary-field-sources p-4">
  <section class="intro mb-4">
    <h1 class="m-0">Shift Summary Field Sources</h1>
    <p class="text-color-secondary mt-2 mb-0">
      Reference for where shift summary fields come from when a draft is saved or submitted.
    </p>
  </section>

  <section class="section">
    <h2>Summary Profiles</h2>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Job Type</th>
            <th>Profile</th>
            <th>Effect</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>TcpFracDrillout, FESCODrillout, FRCSDilloutWashout, EQTDrilloutWashout</td>
            <td>Drillout/Washout</td>
            <td>Uses drillout/washout metric rows and hides HydroLyft-only fluid-system fields.</td>
          </tr>
          <tr>
            <td>HydroLyftJob</td>
            <td>HydroLyft</td>
            <td>Uses HydroLyft metric rows and includes current fluid system, foam stability, and sand suspension.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="section">
    <h2>User-entered Fields</h2>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Source</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Shift Start</td><td>User input</td><td>Required to save.</td></tr>
          <tr><td>Shift End</td><td>User input</td><td>Required to save. Must be after Shift Start.</td></tr>
          <tr><td>Day or Night Shift</td><td>User input</td><td>Defaults to Day.</td></tr>
          <tr><td>Crew Members</td><td>User input</td><td>Options come from company users; selected names are saved on the summary.</td></tr>
          <tr><td>Safety Topic(s) Discussed</td><td>User input</td><td>Optional narrative field.</td></tr>
          <tr><td>Any Safety Incidents?</td><td>User input</td><td>If No, incident details and selected incident types are cleared.</td></tr>
          <tr><td>Safety Incident Details</td><td>User input</td><td>Saved and required to submit when safety incidents are marked Yes.</td></tr>
          <tr><td>Incidents</td><td>User input</td><td>Saved and required to submit when safety incidents are marked Yes.</td></tr>
          <tr><td>Current Status</td><td>User input</td><td>Required to submit.</td></tr>
          <tr><td>Job Status</td><td>User input</td><td>Required to submit.</td></tr>
          <tr><td>Last Discussion with Customer</td><td>User input</td><td>Required to submit.</td></tr>
          <tr><td>Data Components Working?</td><td>User input</td><td>If Yes, the context field is cleared.</td></tr>
          <tr><td>Data Components Context</td><td>User input</td><td>Saved and required to submit when data components are marked not working.</td></tr>
          <tr><td>Fluid Type</td><td>User input</td><td>Label and options vary by summary profile. Required to submit.</td></tr>
          <tr><td>Current Fluid System</td><td>User input</td><td>HydroLyft profile only. Required to submit for HydroLyft.</td></tr>
          <tr><td>Foam Stability Time</td><td>User input</td><td>HydroLyft profile only. Decimal values are supported. Required to submit when Current Fluid System is HydroLyft or TurboLyft.</td></tr>
          <tr><td>Sand Suspension Time</td><td>User input</td><td>HydroLyft profile only. Decimal values are supported. Required to submit when Current Fluid System is HydroLyft or TurboLyft.</td></tr>
          <tr><td>Part C Test Status</td><td>User input</td><td>Yes, No, or No LCM Use Planned.</td></tr>
          <tr><td>Grams to Pass</td><td>User input</td><td>Saved and required to submit when Part C Test Status is Yes or No.</td></tr>
          <tr><td>Water Leak Off</td><td>User input</td><td>Saved and required to submit when Part C Test Status is Yes or No.</td></tr>
          <tr><td>Mechanical Needs</td><td>User input</td><td>Optional narrative field.</td></tr>
          <tr><td>ISCAV Gal Consumed</td><td>User input</td><td>Optional numeric field.</td></tr>
          <tr><td>Procide Gal Consumed</td><td>User input</td><td>Optional numeric field.</td></tr>
          <tr><td>Fuel Provided by Customer?</td><td>User input</td><td>Yes/No field.</td></tr>
          <tr><td>Next Planned Work</td><td>User input</td><td>Optional narrative field.</td></tr>
          <tr><td>Attachments</td><td>User upload</td><td>Files and descriptions are stored with the shift summary. Submit shows a warning when no files are attached.</td></tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="section">
    <h2>Job and Snapshot Fields</h2>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Source</th>
            <th>Rule</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Submitted At</td><td>System</td><td>Set when the summary is submitted.</td></tr>
          <tr><td>Submitted By</td><td>System</td><td>Current signed-in user at submit time.</td></tr>
          <tr><td>Customer</td><td>Job/company data</td><td>Customer company name, with operator name as fallback.</td></tr>
          <tr><td>ESS Job Number</td><td>Job data</td><td>Vendor job name.</td></tr>
          <tr><td>Well Name</td><td>Job data</td><td>Well information name.</td></tr>
          <tr><td>District</td><td>Job data</td><td>Vendor job district.</td></tr>
          <tr><td>Date and Time of Report</td><td>System</td><td>Timestamp when output fields are rebuilt.</td></tr>
          <tr><td>Total Hours Worked</td><td>Calculated</td><td>Shift End minus Shift Start, rounded to 2 decimals.</td></tr>
          <tr><td>Total BBLs Pumped Over Shift</td><td>Live data</td><td>Counter change from HL Flow OUT Total or Pump BBL Total during the shift window.</td></tr>
          <tr><td>CT/WOR Provider</td><td>Job data</td><td>Provider saved on the vendor job.</td></tr>
          <tr><td>DH Tool Provider</td><td>Job data</td><td>Provider saved on the vendor job.</td></tr>
          <tr><td>Tubing Unit</td><td>Job data</td><td>Tubing unit description saved on the vendor job.</td></tr>
          <tr><td>Flowback Provider</td><td>Job data</td><td>Provider saved on the vendor job.</td></tr>
          <tr><td>PBTD</td><td>Job data</td><td>Measured depth from well information.</td></tr>
          <tr><td>Current Depth</td><td>Live data</td><td>Latest Depth value in the shift window.</td></tr>
          <tr><td>Deepest Depth Recorded</td><td>Workbook data</td><td>Maximum Depth value in workbook entries during the shift window.</td></tr>
          <tr><td>Footage Moved During Shift</td><td>Live data</td><td>Difference between first and last Depth values in the shift window.</td></tr>
          <tr><td>ROP Over Shift</td><td>Calculated</td><td>Footage Moved During Shift divided by Total Hours Worked.</td></tr>
          <tr><td>Footage Remaining</td><td>Live data</td><td>Latest DepthLeft value in the shift window.</td></tr>
          <tr><td>Plugs In Well</td><td>Job data</td><td>Frac plug stages from well information.</td></tr>
          <tr><td>Total Plugs Milled</td><td>Live data</td><td>Latest PlugNumber value in the shift window.</td></tr>
          <tr><td>Plugs Remaining</td><td>Live data</td><td>Latest PlugsLeft value in the shift window.</td></tr>
          <tr><td>Dashboard URL</td><td>System</td><td>Grafana dashboard link scoped to the shift window.</td></tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="section">
    <h2>Live Data Status</h2>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Source</th>
            <th>Rule</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Live Data Pushed Over Shift</td><td>Live data</td><td>Percent of one-minute shift buckets that contain device data.</td></tr>
          <tr><td>Device</td><td>Job/device data</td><td>Devices assigned to the vendor job.</td></tr>
          <tr><td>Last Data Pushed</td><td>Live data</td><td>Latest data timestamp per assigned device during the shift window.</td></tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="section">
    <h2>Live Data Report</h2>
    <p class="text-color-secondary">
      Live data rows use values recorded during the shift window. Current is the latest value; Avg Over Shift is the average of numeric values.
    </p>

    <h3>HydroLyft</h3>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Live Channel</th>
            <th>Rule</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Pump Rate</td><td>HL Flowrate OUT, PumpRate</td><td>Average numeric values.</td></tr>
          <tr><td>Return Rate</td><td>FlowbackRate, Return Rate, HL Return Rate, IFM Return Rate, D3 Return Rate</td><td>Average numeric values across matching return-rate channels.</td></tr>
          <tr><td>HL Circulation Pressure PSI</td><td>HL Circulation Pressure PSI</td><td>Average numeric values; canonical pressure is fallback if the HL channel is unavailable.</td></tr>
          <tr><td>HL Wellhead Pressure PSI</td><td>HL Wellhead Pressure PSI</td><td>Average numeric values; canonical pressure is fallback if the HL channel is unavailable.</td></tr>
          <tr><td>HL Differential PSI</td><td>HL Differential PSI</td><td>Average numeric values; canonical pressure is fallback if the HL channel is unavailable.</td></tr>
          <tr><td>Manifold Pressure In</td><td>HL Manifold IN PSI</td><td>Average numeric values.</td></tr>
          <tr><td>Manifold Pressure Out</td><td>HL Manifold OUT PSI</td><td>Average numeric values.</td></tr>
          <tr><td>Manifold Differential</td><td>HL Manifold Diff PSI</td><td>Average numeric values.</td></tr>
          <tr><td>Air Rate</td><td>HL Manifold Air Vacuum SCFM</td><td>Average numeric values.</td></tr>
          <tr><td>N2 Purity</td><td>HL N2 Purity</td><td>Average numeric values.</td></tr>
          <tr><td>Discharge PSI</td><td>HL Discharge PSI</td><td>Average numeric values.</td></tr>
        </tbody>
      </table>
    </div>

    <h3>Drillout/Washout</h3>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Live Channel</th>
            <th>Rule</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Pump Rate</td><td>HL Flowrate OUT, PumpRate</td><td>Average numeric values.</td></tr>
          <tr><td>Return Rate</td><td>FlowbackRate, Return Rate, HL Return Rate, IFM Return Rate, D3 Return Rate</td><td>Average numeric values across matching return-rate channels.</td></tr>
          <tr><td>Circulation Pressure PSI</td><td>Circulation Pressure PSI, Circ PSI</td><td>Average numeric values; canonical pressure is fallback if raw channels are unavailable.</td></tr>
          <tr><td>Wellhead Pressure PSI</td><td>Wellhead Pressure PSI, Wellhead PSI</td><td>Average numeric values; canonical pressure is fallback if raw channels are unavailable.</td></tr>
          <tr><td>Differential PSI</td><td>Differential PSI</td><td>Average numeric values; canonical pressure is fallback if the raw channel is unavailable.</td></tr>
          <tr><td>Discharge PSI</td><td>Discharge PSI</td><td>Average numeric values.</td></tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="section">
    <h2>Workbook Data Report</h2>
    <p class="text-color-secondary">
      Workbook data rows use workbook entries recorded during the shift window. Current is the latest numeric value; Avg Over Shift is the average of numeric values.
    </p>

    <h3>HydroLyft</h3>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Workbook Measurement</th>
            <th>Rule</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Pump Rate</td><td>PumpRate</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Return Rate</td><td>FlowbackRate</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Horizontal AVs</td><td>Casing1HorizontalAnnularVelocity</td><td>Live-derived value displayed in the workbook report.</td></tr>
          <tr><td>Visc In</td><td>FunnelViscosity</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Visc Out</td><td>ReturnViscosity</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Density In</td><td>FluidDensity, DensityinTubs</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Density Out</td><td>ReturnDensity</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Sand Percentage</td><td>SandContentPercentage</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Chlorides</td><td>Chlorides</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>pH</td><td>Ph</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>CirculatingPressure</td><td>CirculatingPressure</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>WellheadPressure</td><td>WellheadPressure</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>DifferentialPressure</td><td>DifferentialPressure</td><td>Direct workbook values when present; otherwise calculated from workbook CirculatingPressure minus WellheadPressure.</td></tr>
        </tbody>
      </table>
    </div>

    <h3>Drillout/Washout</h3>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Workbook Measurement</th>
            <th>Rule</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Pump Rate</td><td>PumpRate</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Return Rate</td><td>FlowbackRate</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Horizontal AVs</td><td>Casing1HorizontalAnnularVelocity</td><td>Live-derived value displayed in the workbook report.</td></tr>
          <tr><td>Visc In</td><td>FunnelViscosity</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Density In</td><td>FluidDensity, DensityinTubs</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>Chlorides</td><td>Chlorides</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>pH</td><td>Ph</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>CirculatingPressure</td><td>CirculatingPressure</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>WellheadPressure</td><td>WellheadPressure</td><td>Workbook values during the shift window.</td></tr>
          <tr><td>DifferentialPressure</td><td>CirculatingPressure minus WellheadPressure</td><td>Calculated from workbook rows where both values are present.</td></tr>
          <tr><td>Re#</td><td>Casing1HorizontalSlwRe</td><td>Live-derived value displayed in the workbook report.</td></tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="section">
    <h2>LCM Report</h2>
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Source</th>
            <th>Rule</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Part C Test Status</td><td>User input</td><td>Saved on the summary.</td></tr>
          <tr><td>Grams to Pass</td><td>User input</td><td>Shown when Part C Test Status is Yes.</td></tr>
          <tr><td>Water Leak Off</td><td>User input</td><td>Shown when Part C Test Status is Yes.</td></tr>
          <tr><td>BBLs of LCM Pumped</td><td>Workbook data</td><td>Sum of LCM Pill Size (bbls) during the shift window.</td></tr>
          <tr><td>LBS of Part C</td><td>Workbook data</td><td>Sum of Lbs of Part C during the shift window.</td></tr>
          <tr><td>Avg LBS Part C / BBL</td><td>Calculated</td><td>LBS of Part C divided by BBLs of LCM Pumped.</td></tr>
          <tr><td>LBS of OBP</td><td>Workbook data</td><td>Sum of Lbs of OBP during the shift window.</td></tr>
          <tr><td>Avg LBS OBP / BBL</td><td>Calculated</td><td>LBS of OBP divided by BBLs of LCM Pumped.</td></tr>
          <tr><td>LBS of Fiber</td><td>Workbook data</td><td>Sum of Lbs of Fiber during the shift window.</td></tr>
          <tr><td>Avg LBS Fiber / BBL</td><td>Calculated</td><td>LBS of Fiber divided by BBLs of LCM Pumped.</td></tr>
        </tbody>
      </table>
    </div>
  </section>
</main>
